import React from "react";
import { Box, Typography, ButtonBase } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "../../hooks/useTranslation";

interface RepresentationStepProps {
  hasAttorney: string;
  onChange: (field: string, value: string) => void;
}

const RepresentationStep: React.FC<RepresentationStepProps> = ({
  hasAttorney,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ py: 2 }}>
      {/* Question */}
      <Typography
        variant="h5"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#374151",
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        {t("form.secondQuestion.question")}
      </Typography>

      {/* Icon Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: { xs: 2, sm: 6 },
          flexWrap: "nowrap",
        }}
      >
        {/* No Button (No attorney - this is the positive outcome) */}
        <ButtonBase
          onClick={() => onChange("hasAttorney", "NO")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 100, sm: 150 },
              height: { xs: 100, sm: 150 },
              borderRadius: "50%",
              backgroundColor: hasAttorney === "NO" ? "#dcfce7" : "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              border:
                hasAttorney === "NO"
                  ? "3px solid #22c55e"
                  : "3px solid transparent",
              "&:hover": {
                backgroundColor: "#dcfce7",
              },
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: { xs: 60, sm: 80 },
                color: "#22c55e",
              }}
            />
          </Box>
          <Typography
            sx={{
              color: "#0ea5e9",
              fontWeight: 500,
              fontSize: "1.1rem",
              textDecoration: "underline",
              textUnderlineOffset: "4px",
            }}
          >
            {t("form.secondQuestion.option1")}
          </Typography>
        </ButtonBase>

        {/* Yes Button (Has attorney - this disqualifies) */}
        <ButtonBase
          onClick={() => onChange("hasAttorney", "YES")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 100, sm: 150 },
              height: { xs: 100, sm: 150 },
              borderRadius: "50%",
              backgroundColor: hasAttorney === "YES" ? "#fee2e2" : "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              border:
                hasAttorney === "YES"
                  ? "3px solid #ef4444"
                  : "3px solid transparent",
              "&:hover": {
                backgroundColor: "#fee2e2",
              },
            }}
          >
            <CancelIcon
              sx={{
                fontSize: { xs: 60, sm: 80 },
                color: "#ef4444",
              }}
            />
          </Box>
          <Typography
            sx={{
              color: "#0ea5e9",
              fontWeight: 500,
              fontSize: "1.1rem",
              textDecoration: "underline",
              textUnderlineOffset: "4px",
            }}
          >
            {t("form.secondQuestion.option2")}
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default RepresentationStep;
